import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { ApiPath, RoleCode } from '@app/shared';
import { AdminLayout, getEffectiveRole } from '../components/admin-layout';
import { Card } from '../components/ui';
import { useAuth } from '../features/auth/use-auth';
import { apiGet } from '../lib/api';

const cardStyle: React.CSSProperties = {
  border: '1px solid #E2E8F0',
  boxShadow: 'none',
  minHeight: '112px',
};

function StatCard({ label, value, context }: { label: string; value: string; context: string }) {
  return (
    <Card style={cardStyle}>
      <div style={{ color: '#64748B', fontSize: '0.75rem', fontWeight: 500 }}>{label}</div>
      <div style={{ marginTop: '0.5rem', fontSize: '1.5rem', fontWeight: 600 }}>{value}</div>
      <div style={{ marginTop: '0.5rem', color: '#475569', fontSize: '0.875rem' }}>{context}</div>
    </Card>
  );
}

function CardGrid({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '1rem',
      }}
    >
      {children}
    </div>
  );
}

const pegawaiListSchema = z.object({
  data: z.array(
    z.object({
      id: z.number(),
      nama: z.string(),
      roles: z.array(z.string()).optional(),
      jabatan_nama: z.string().nullable().optional(),
    }),
  ),
  total: z.number().optional(),
  pagination: z.object({ total: z.number() }).optional(),
});

const absensiTodaySchema = z.object({
  data: z.array(z.object({ status: z.string() })).optional(),
  total: z.number().optional(),
});

const absensiStatusSchema = z.object({
  data: z.object({
    sudahCheckIn: z.boolean(),
    status: z.string().nullable().optional(),
    checkInAt: z.string().nullable().optional(),
    checkOutAt: z.string().nullable().optional(),
  }),
});

const riwayatSchema = z.object({
  data: z.array(z.object({ tanggal: z.string(), status: z.string() })),
});

const periodeListSchema = z.object({
  data: z.array(z.object({ id: z.number(), nama: z.string(), status: z.string() })),
});

const sekolahListSchema = z.object({ data: z.array(z.object({ id: z.number() })) });

const num = (n: number | undefined | null) => (typeof n === 'number' ? String(n) : '-');

function useLoad<T>(loader: () => Promise<T>) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    loader()
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Gagal memuat'));
    // Run once on mount; loader is stable per component.
  }, []);
  return { data, error };
}

function AdminDashboard() {
  const pegawai = useLoad(() =>
    apiGet(`${ApiPath.MASTER.PEGAWAI}?page=1&pageSize=200`, pegawaiListSchema),
  );
  const sekolah = useLoad(() =>
    apiGet(`${ApiPath.MASTER.SEKOLAH}?page=1&pageSize=200`, sekolahListSchema),
  );
  const today = useLoad(() =>
    apiGet(`${ApiPath.ABSENSI.TODAY}?page=1&pageSize=500`, absensiTodaySchema).catch(() => null),
  );
  const periode = useLoad(() =>
    apiGet(`${ApiPath.ABSENSI.PERIODE}?page=1&pageSize=50`, periodeListSchema).catch(() => null),
  );

  const list = pegawai.data?.data ?? [];
  const totalPegawai = pegawai.data?.pagination?.total ?? pegawai.data?.total ?? list.length;
  const guru = list.filter(
    (p) => p.roles?.includes('guru') || p.jabatan_nama?.toLowerCase().includes('guru'),
  ).length;
  const kepsek = list.filter((p) => p.roles?.includes('kepala_sekolah')).length;
  const totalSekolah = sekolah.data?.data.length ?? null;
  const hadirHariIni = today.data?.data?.filter((r) => r.status === 'hadir').length ?? null;
  const belumAbsen = hadirHariIni !== null ? Math.max(0, totalPegawai - hadirHariIni) : null;
  const periodeAktif = periode.data?.data.find((p) => p.status === 'aktif')?.nama ?? '-';

  return (
    <CardGrid>
      <StatCard
        label="Total Pegawai"
        value={num(totalPegawai)}
        context="Pegawai aktif terdaftar."
      />
      <StatCard label="Total Guru" value={num(guru)} context="Pegawai dengan peran guru." />
      <StatCard
        label="Kepala Sekolah"
        value={num(kepsek)}
        context="Pegawai dengan peran kepala sekolah."
      />
      <StatCard label="Total Sekolah" value={num(totalSekolah)} context="Unit sekolah terdaftar." />
      <StatCard
        label="Hadir Hari Ini"
        value={num(hadirHariIni)}
        context="Pegawai sudah absensi hadir."
      />
      <StatCard
        label="Belum Absensi"
        value={num(belumAbsen)}
        context="Estimasi pegawai belum absen."
      />
      <StatCard
        label="Periode Honor Aktif"
        value={periodeAktif}
        context="Periode absensi/honor berjalan."
      />
    </CardGrid>
  );
}

function GuruDashboard() {
  const status = useLoad(() =>
    apiGet(ApiPath.ABSENSI.STATUS, absensiStatusSchema).catch(() => null),
  );
  const riwayat = useLoad(() =>
    apiGet(`${ApiPath.ABSENSI.RIWAYAT}?page=1&pageSize=5`, riwayatSchema).catch(() => null),
  );

  const s = status.data?.data;
  const statusLabel = !s
    ? '-'
    : s.sudahCheckIn
      ? s.checkOutAt
        ? 'Selesai (Check-out)'
        : 'Hadir (Sudah Check-in)'
      : 'Belum Absensi';
  const recent = riwayat.data?.data ?? [];

  return (
    <>
      <CardGrid>
        <StatCard
          label="Status Absensi Hari Ini"
          value={statusLabel}
          context="Status check-in/check-out pribadi."
        />
        <StatCard
          label="Riwayat Terakhir"
          value={recent.length ? `${recent.length} catatan` : '-'}
          context={
            recent[0]
              ? `Terakhir: ${recent[0].tanggal} (${recent[0].status})`
              : 'Belum ada riwayat.'
          }
        />
      </CardGrid>
      <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem' }}>
        <Link to="/absensi" style={{ color: '#1D4ED8', fontWeight: 600 }}>
          Catat Hadir
        </Link>
        <Link to="/profil-saya" style={{ color: '#1D4ED8', fontWeight: 600 }}>
          Lihat Profil
        </Link>
      </div>
    </>
  );
}

function KepalaSekolahDashboard() {
  const today = useLoad(() =>
    apiGet(`${ApiPath.ABSENSI.TODAY}?page=1&pageSize=500`, absensiTodaySchema).catch(() => null),
  );
  const pegawai = useLoad(() =>
    apiGet(`${ApiPath.MASTER.PEGAWAI}?page=1&pageSize=200`, pegawaiListSchema).catch(() => null),
  );

  const rows = today.data?.data ?? [];
  const hadir = rows.filter((r) => r.status === 'hadir').length;
  const totalGuru = pegawai.data?.data.length ?? null;
  const ringkasan = ['hadir', 'izin', 'sakit', 'alpha']
    .map((k) => `${k}: ${rows.filter((r) => r.status === k).length}`)
    .join(', ');

  return (
    <>
      <CardGrid>
        <StatCard
          label="Ringkasan Absensi"
          value={rows.length ? 'Hari ini' : '-'}
          context={rows.length ? ringkasan : 'Belum ada data hari ini.'}
        />
        <StatCard
          label="Jumlah Guru"
          value={num(totalGuru)}
          context="Pegawai dalam scope sekolah."
        />
        <StatCard
          label="Hadir Hari Ini"
          value={num(rows.length ? hadir : null)}
          context="Pegawai hadir di sekolah."
        />
      </CardGrid>
      <div style={{ marginTop: '1rem' }}>
        <Link to="/profil-saya" style={{ color: '#1D4ED8', fontWeight: 600 }}>
          Lihat Profil
        </Link>
      </div>
    </>
  );
}

export function DashboardPage() {
  const { user } = useAuth();
  const effectiveRole = getEffectiveRole(user?.roles);

  return (
    <AdminLayout title="Dashboard">
      <Card style={{ border: '1px solid #E2E8F0', boxShadow: 'none', marginBottom: '1rem' }}>
        <div style={{ fontSize: '1rem', fontWeight: 600 }}>
          Selamat datang, {user?.nama ?? 'Pengguna'}.
        </div>
        <div style={{ marginTop: '0.375rem', color: '#475569', fontSize: '0.875rem' }}>
          Ringkasan informasi sesuai peran Anda.
        </div>
      </Card>

      {effectiveRole === RoleCode.ADMIN && <AdminDashboard />}
      {effectiveRole === RoleCode.GURU && <GuruDashboard />}
      {effectiveRole === RoleCode.KEPALA_SEKOLAH && <KepalaSekolahDashboard />}
    </AdminLayout>
  );
}
